import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Search, Download, RefreshCw, ChevronLeft, ChevronRight,
    ArrowUpDown, FileSpreadsheet, Database, Eye, X, Layers,
    Hash, Tag, Factory, Globe, Package, Loader2, XCircle,
    Filter, ChevronDown, ChevronUp, Save, Pencil, Undo2, Check, Plus, Trash2, Upload, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '../lib/api';
import * as XLSX from 'xlsx';

// ── Column display config — matches Excel export format ──────────────
const MAX_PROPERTIES = 30;
const PAGE_SIZE_OPTIONS = [20, 50, 100, 250, 500];
const IMPORT_BATCH_SIZE = 100;

const EMPTY_IMPORT_STATUS = {
    open: false, running: false, total: 0, processed: 0,
    inserted: 0, updated: 0, skipped: 0, failed: 0,
    skippedRows: [], failedRows: [], title: 'Import source data',
};

const formatSourceId = (record) => {
    const numericId = Number(record?.id ?? record?.ID);
    return Number.isSafeInteger(numericId) && numericId > 0 ? `ENVSF${10000000 + numericId}` : '—';
};

const SOURCE_IMPORT_HEADER_ALIASES = new Map([
    ['noun/modifier', 'NOUN/MODIFIER'],
    ['noun modifier', 'NOUN/MODIFIER'],
    ['noun_modifier', 'NOUN/MODIFIER'],
    ['nounmodifier', 'NOUN/MODIFIER'],
    ['n/m', 'NOUN/MODIFIER'],
    ['part number', 'Part Number'],
    ['partnumber', 'Part Number'],
    ['part no', 'Part Number'],
    ['part no.', 'Part Number'],
]);

const canonicalImportHeader = (header) => {
    const trimmed = String(header || '').trim();
    return SOURCE_IMPORT_HEADER_ALIASES.get(trimmed.toLowerCase()) || trimmed;
};

const EXPORT_HEADERS = [
    'ID', 'Part Number', 'Description 1', 'Description 2', 'given_Supplier Name', 'given_Supplier Partnumber',
    'given_Supplier Name2', 'given_Supplier Partnumber2',
    'given_Manufacture Name1', 'given_Manufacturers part number1',
    'given_Manufacturers part number2', 'given_Model',
    'NOUN/MODIFIER', 'CLEANSED/ENRICHED', 'BRAND NAME',
    'MANUFACTURER NAME 1', 'MANUFACTURER PART NUMBER 1', 'MANUFACTURER URL 1',
    'MANUFACTURER NAME 2', 'MANUFACTURER PART NUMBER 2', 'MANUFACTURER URL 2',
    'MANUFACTURER NAME 3', 'MANUFACTURER PART NUMBER 3', 'MANUFACTURER URL 3',
    'VENDOR NAME', 'VENDOR PART NUMBER', 'VENDOR URL',
    'VENDOR NAME 2', 'VENDOR PART NUMBER 2', 'VENDOR URL 2',
    'VENDOR NAME 3', 'VENDOR PART NUMBER 3', 'VENDOR URL 3',
    'VENDOR NAME 4', 'VENDOR PART NUMBER 4', 'VENDOR URL 4',
    'VENDOR NAME 5', 'VENDOR PART NUMBER 5', 'VENDOR URL 5', 'UNUSED_TEXT',
    ...Array.from({ length: MAX_PROPERTIES }, (_, i) => [`PROPERTY${i + 1}`, `VALUE_OF_PROPERTY${i + 1}`]).flat(),
    '_Status', '_Source', 'Unmatched',
];

// Visible table columns for the main grid
const TABLE_COLUMNS = [
    { key: 'Part Number',                   label: 'Part Number',       sortable: true,  maxLen: 25, bold: true },
    { key: 'Description 1',                 label: 'Description 1',     sortable: true,  maxLen: 40 },
    { key: 'Description 2',                 label: 'Description 2',     sortable: true,  maxLen: 40 },
    { key: 'given_Supplier Name',            label: 'Supplier',          sortable: true,  maxLen: 25 },
    { key: 'given_Supplier Partnumber',      label: 'Supplier PN',       sortable: true,  maxLen: 25, bold: true },
    { key: 'NOUN/MODIFIER',                 label: 'Noun/Modifier',     sortable: true,  maxLen: 30 },
    { key: 'MANUFACTURER NAME 1',           label: 'MFR Name',          sortable: true,  maxLen: 20 },
    { key: 'MANUFACTURER PART NUMBER 1',    label: 'MFR PN',            sortable: true,  maxLen: 25, bold: true },
    { key: 'MANUFACTURER URL 1',            label: 'MFR URL',           sortable: false, isUrl: true },
    { key: 'VENDOR NAME',                   label: 'Vendor',            sortable: true,  maxLen: 20 },
    { key: 'VENDOR PART NUMBER',            label: 'Vendor PN',         sortable: true,  maxLen: 25 },
];

// All DB columns for detail view — matches new DB schema exactly
const DETAIL_COLUMNS = [
    // Input data
    'Part Number', 'Description 1', 'Description 2', 'given_Supplier Name', 'given_Supplier Partnumber',
    'given_Supplier Name2', 'given_Supplier Partnumber2',
    'given_Manufacture Name1', 'given_Manufacturers part number1',
    'given_Manufacturers part number2', 'given_Model',
    // AI output
    'NOUN/MODIFIER', 'CLEANSED/ENRICHED', 'BRAND NAME',
    'MANUFACTURER NAME 1', 'MANUFACTURER PART NUMBER 1', 'MANUFACTURER URL 1',
    'MANUFACTURER NAME 2', 'MANUFACTURER PART NUMBER 2', 'MANUFACTURER URL 2',
    'MANUFACTURER NAME 3', 'MANUFACTURER PART NUMBER 3', 'MANUFACTURER URL 3',
    'VENDOR NAME', 'VENDOR PART NUMBER', 'VENDOR URL',
    'VENDOR NAME 2', 'VENDOR PART NUMBER 2', 'VENDOR URL 2',
    'VENDOR NAME 3', 'VENDOR PART NUMBER 3', 'VENDOR URL 3',
    'VENDOR NAME 4', 'VENDOR PART NUMBER 4', 'VENDOR URL 4',
    'VENDOR NAME 5', 'VENDOR PART NUMBER 5', 'VENDOR URL 5',
];

// Input-only fields (highlighted in amber in detail view)
const INPUT_FIELD_KEYS = [
    'Part Number', 'Description 1', 'Description 2', 'given_Supplier Name', 'given_Supplier Partnumber',
    'given_Supplier Name2', 'given_Supplier Partnumber2',
    'given_Manufacture Name1', 'given_Manufacturers part number1',
    'given_Manufacturers part number2', 'given_Model',
];

// Short labels for detail view
const SHORT_LABELS = {
    'Part Number': 'Part Number',
    'Description 1': 'Description 1',
    'Description 2': 'Description 2',
    'given_Supplier Name': 'Supplier Name',
    'given_Supplier Partnumber': 'Supplier Part Number',
    'given_Supplier Name2': 'Supplier Name 2',
    'given_Supplier Partnumber2': 'Supplier Part Number 2',
    'given_Manufacture Name1': 'Manufacturer Name (given)',
    'given_Manufacturers part number1': 'MFR Part Number (given)',
    'given_Manufacturers part number2': 'MFR Part Number 2 (given)',
    'given_Model': 'Model (given)',
    'NOUN/MODIFIER': 'Noun / Modifier',
    'CLEANSED/ENRICHED': 'Cleansed / Enriched',
    'BRAND NAME': 'Brand Name',
    'MANUFACTURER NAME 1': 'Manufacturer 1',
    'MANUFACTURER PART NUMBER 1': 'MFR Part Number 1',
    'MANUFACTURER URL 1': 'MFR URL 1',
    'MANUFACTURER NAME 2': 'Manufacturer 2',
    'MANUFACTURER PART NUMBER 2': 'MFR Part Number 2',
    'MANUFACTURER URL 2': 'MFR URL 2',
    'MANUFACTURER NAME 3': 'Manufacturer 3',
    'MANUFACTURER PART NUMBER 3': 'MFR Part Number 3',
    'MANUFACTURER URL 3': 'MFR URL 3',
    'VENDOR NAME': 'Vendor 1',
    'VENDOR PART NUMBER': 'Vendor Part Number 1',
    'VENDOR URL': 'Vendor URL 1',
    'VENDOR NAME 2': 'Vendor 2',
    'VENDOR PART NUMBER 2': 'Vendor Part Number 2',
    'VENDOR URL 2': 'Vendor URL 2',
    'VENDOR NAME 3': 'Vendor 3',
    'VENDOR PART NUMBER 3': 'Vendor Part Number 3',
    'VENDOR URL 3': 'Vendor URL 3',
    'VENDOR NAME 4': 'Vendor 4',
    'VENDOR PART NUMBER 4': 'Vendor Part Number 4',
    'VENDOR URL 4': 'Vendor URL 4',
    'VENDOR NAME 5': 'Vendor 5',
    'VENDOR PART NUMBER 5': 'Vendor Part Number 5',
    'VENDOR URL 5': 'Vendor URL 5',
};

// ── Helpers ──────────────────────────────────────────────────────────
const TruncatedCell = ({ text, maxLen = 40 }) => {
    if (!text) return <span className="text-slate-400">—</span>;
    const str = String(text);
    if (str.length <= maxLen) return <span>{str}</span>;
    return <span title={str}>{str.slice(0, maxLen)}…</span>;
};

const UrlCell = ({ url }) => {
    if (!url) return <span className="text-slate-400">—</span>;
    const str = String(url);
    return (
        <a href={str} target="_blank" rel="noopener noreferrer"
           className="text-primary-600 hover:text-primary-800 hover:underline text-xs break-all"
           title={str}>
            {str.length > 30 ? str.slice(0, 30) + '…' : str}
        </a>
    );
};

function getPropertyPairs(rec) {
    const pairs = [];
    for (let i = 1; i <= MAX_PROPERTIES; i++) {
        const prop = rec[`PROPERTY${i}`];
        const val = rec[`VALUE_OF_PROPERTY${i}`];
        if (prop && String(prop).trim()) {
            pairs.push({ name: String(prop).trim(), value: val || '' });
        }
    }
    return pairs;
}

// ══════════════════════════════════════════════════════════════════════
//  EDITABLE FIELD COMPONENT
// ══════════════════════════════════════════════════════════════════════
function EditableField({ label, value, isEditing, onChange, isUrl, isModified, isInput }) {
    const displayValue = value || '';
    
    if (!isEditing) {
        return (
            <div className={`flex gap-3 py-2.5 px-4 hover:bg-slate-50/60 transition-colors ${isModified ? 'bg-amber-50/40' : ''}`}>
                <span className={`text-xs font-semibold min-w-[160px] shrink-0 pt-0.5 ${isInput ? 'text-amber-700/80' : 'text-slate-500'}`}>
                    {label}
                    {isModified && <span className="ml-1 text-amber-500">●</span>}
                </span>
                {isUrl && displayValue ? (
                    <a href={displayValue} target="_blank" rel="noopener noreferrer"
                       className="text-sm text-primary-600 hover:underline break-all">{displayValue}</a>
                ) : (
                    <span className="text-sm text-slate-800 break-all">{displayValue || <span className="text-slate-400">—</span>}</span>
                )}
            </div>
        );
    }

    return (
        <div className={`flex gap-3 py-2 px-4 items-start ${isModified ? 'bg-amber-50/40' : ''}`}>
            <span className={`text-xs font-semibold min-w-[160px] shrink-0 pt-2.5 ${isInput ? 'text-amber-700/80' : 'text-slate-500'}`}>
                {label}
                {isModified && <span className="ml-1 text-amber-500">●</span>}
            </span>
            <input
                type="text"
                value={displayValue}
                onChange={(e) => onChange(e.target.value)}
                className={`flex-1 px-3 py-1.5 bg-white border rounded-lg text-sm text-slate-800
                           focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400 transition-all
                           ${isModified ? 'border-amber-300 bg-amber-50/30' : 'border-slate-200'}`}
            />
        </div>
    );
}

// ══════════════════════════════════════════════════════════════════════
//  DETAIL MODAL — Full-width with Prev/Next navigation + EDIT mode
// ══════════════════════════════════════════════════════════════════════
function RecordDetailModal({ records, currentIndex, onClose, onNavigate, onSave, onDelete }) {
    const record = records[currentIndex];
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({});
    const [editProps, setEditProps] = useState([]);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    
    if (!record) return null;

    const hasPrev = currentIndex > 0;
    const hasNext = currentIndex < records.length - 1;

    // Initialize edit data when entering edit mode or when record changes
    const startEditing = () => {
        const data = {};
        DETAIL_COLUMNS.forEach(col => {
            data[col] = record[col] || '';
        });
        setEditData(data);
        setEditProps(getPropertyPairs(record).map(p => ({ ...p })));
        setIsEditing(true);
    };

    const cancelEditing = () => {
        setIsEditing(false);
        setEditData({});
        setEditProps([]);
    };

    const handleFieldChange = (key, value) => {
        setEditData(prev => ({ ...prev, [key]: value }));
    };

    const handlePropChange = (index, field, value) => {
        setEditProps(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const addProperty = () => {
        if (editProps.length >= MAX_PROPERTIES) {
            toast.warning(`Maximum ${MAX_PROPERTIES} properties allowed`);
            return;
        }
        setEditProps(prev => [...prev, { name: '', value: '' }]);
    };

    const removeProperty = (index) => {
        setEditProps(prev => prev.filter((_, i) => i !== index));
    };

    const isFieldModified = (key) => {
        if (!isEditing) return false;
        const original = record[key] || '';
        const current = editData[key] || '';
        return String(original) !== String(current);
    };

    const hasChanges = () => {
        if (!isEditing) return false;
        // Check core fields
        for (const col of DETAIL_COLUMNS) {
            if (String(record[col] || '') !== String(editData[col] || '')) return true;
        }
        // Check properties
        const origProps = getPropertyPairs(record);
        if (origProps.length !== editProps.length) return true;
        for (let i = 0; i < editProps.length; i++) {
            if (!origProps[i]) return true;
            if (origProps[i].name !== editProps[i].name || origProps[i].value !== editProps[i].value) return true;
        }
        return false;
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            // Build the flat record to save
            const flatRecord = { ...record, ...editData };
            
            // Clear all existing properties first
            for (let i = 1; i <= MAX_PROPERTIES; i++) {
                flatRecord[`PROPERTY${i}`] = '';
                flatRecord[`VALUE_OF_PROPERTY${i}`] = '';
            }
            // Set edited properties
            editProps.forEach((prop, i) => {
                if (prop.name.trim()) {
                    flatRecord[`PROPERTY${i + 1}`] = prop.name.trim();
                    flatRecord[`VALUE_OF_PROPERTY${i + 1}`] = prop.value || '';
                }
            });

            await apiClient.saveMajorRecord({
                record: flatRecord,
                forceOverwrite: true,
                existingId: record.id || record.ID,
            });

            toast.success('Record saved successfully');
            setIsEditing(false);
            if (onSave) onSave(); // refresh data
        } catch (err) {
            console.error('Save failed:', err);
            toast.error(`Save failed: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        const recordId = record.id || record.ID;
        const identifier = record['MANUFACTURER PART NUMBER 1'] || record['given_Supplier Partnumber'] || `ID #${recordId}`;
        if (!window.confirm(`Are you sure you want to delete "${identifier}"?\n\nThis action cannot be undone.`)) return;
        
        setDeleting(true);
        try {
            await apiClient.deleteMajorRecordById(recordId);
            toast.success(`Record "${identifier}" deleted`);
            onClose();
            if (onDelete) onDelete();
        } catch (err) {
            console.error('Delete failed:', err);
            toast.error(`Delete failed: ${err.message}`);
        } finally {
            setDeleting(false);
        }
    };

    const handleNavigate = (idx) => {
        if (isEditing && hasChanges()) {
            if (!window.confirm('You have unsaved changes. Discard and navigate?')) return;
        }
        setIsEditing(false);
        setEditData({});
        setEditProps([]);
        onNavigate(idx);
    };

    const allFields = DETAIL_COLUMNS.map(col => ({
        key: col,
        label: SHORT_LABELS[col] || col,
        value: isEditing ? (editData[col] ?? '') : (record[col] || ''),
        isUrl: col.includes('URL'),
        isInput: INPUT_FIELD_KEYS.includes(col),
    }));

    const inputFields = allFields.filter(f => f.isInput);
    const outputFields = allFields.filter(f => !f.isInput);
    const propPairs = isEditing ? editProps : getPropertyPairs(record);
    const mfrPN = record['MANUFACTURER PART NUMBER 1'] || record['given_Supplier Partnumber'] || `#${record.id || record.ID}`;
    const sourceId = formatSourceId(record);

    // Keyboard navigation
    useEffect(() => {
        const handleKey = (e) => {
            if (isEditing) {
                if (e.key === 'Escape') cancelEditing();
                return;
            }
            if (e.key === 'Escape') onClose();
            else if (e.key === 'ArrowLeft' && hasPrev) handleNavigate(currentIndex - 1);
            else if (e.key === 'ArrowRight' && hasNext) handleNavigate(currentIndex + 1);
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [currentIndex, hasPrev, hasNext, isEditing]);

    return (
        <div className="fixed inset-0 z-50 flex items-stretch bg-slate-900/50 backdrop-blur-sm animate-fade-in">
            {/* Full-width modal */}
            <div className="relative bg-white w-full max-w-6xl mx-auto my-4 rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-slide-up">
                
                {/* ── Header ──────────────────────────────── */}
                <div className="shrink-0 flex items-center justify-between px-6 py-4 bg-gradient-to-r from-slate-50 to-white border-b border-slate-200">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${isEditing ? 'bg-amber-100' : 'bg-primary-100'}`}>
                            {isEditing ? <Pencil className="w-5 h-5 text-amber-600" /> : <Eye className="w-5 h-5 text-primary-600" />}
                        </div>
                        <div className="min-w-0">
                            <h3 className="text-lg font-bold text-slate-800 truncate">{mfrPN}</h3>
                            <p className="text-xs text-slate-500">
                                {sourceId} · Record {currentIndex + 1} of {records.length}
                                {record['NOUN/MODIFIER'] && <> · <span className="font-medium text-slate-600">{record['NOUN/MODIFIER']}</span></>}
                                {isEditing && <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700">EDITING</span>}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                        {/* Delete */}
                        {!isEditing && (
                            <button onClick={handleDelete} disabled={deleting}
                                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold
                                           bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 transition-all
                                           disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Delete this record">
                                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                {deleting ? 'Deleting...' : 'Delete'}
                            </button>
                        )}

                        {/* Edit / Save / Cancel */}
                        {!isEditing ? (
                            <button onClick={startEditing}
                                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold
                                           bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 transition-all">
                                <Pencil className="w-4 h-4" /> Edit
                            </button>
                        ) : (
                            <>
                                <button onClick={cancelEditing}
                                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold
                                               bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all">
                                    <Undo2 className="w-4 h-4" /> Cancel
                                </button>
                                <button onClick={handleSave} disabled={saving || !hasChanges()}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold
                                               bg-emerald-600 text-white hover:bg-emerald-700 transition-all shadow-sm
                                               disabled:opacity-50 disabled:cursor-not-allowed">
                                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                    {saving ? 'Saving...' : 'Save'}
                                </button>
                            </>
                        )}

                        <div className="w-px h-8 bg-slate-200 mx-1" />

                        {/* Prev / Next */}
                        <button onClick={() => handleNavigate(currentIndex - 1)} disabled={!hasPrev}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold
                                       bg-slate-100 text-slate-700 hover:bg-slate-200 transition-all
                                       disabled:opacity-30 disabled:cursor-not-allowed">
                            <ChevronLeft className="w-4 h-4" /> Prev
                        </button>
                        <button onClick={() => handleNavigate(currentIndex + 1)} disabled={!hasNext}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold
                                       bg-slate-100 text-slate-700 hover:bg-slate-200 transition-all
                                       disabled:opacity-30 disabled:cursor-not-allowed">
                            Next <ChevronRight className="w-4 h-4" />
                        </button>
                        <div className="w-px h-8 bg-slate-200 mx-1" />
                        <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 transition-colors" title="Close (Esc)">
                            <X size={18} className="text-slate-500" />
                        </button>
                    </div>
                </div>

                {/* ── Body ─────────────────────────────────── */}
                <div className="flex-1 overflow-y-auto px-6 py-5">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        
                        {/* Left: Core Fields */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 mb-1">
                                <Database className="w-4 h-4 text-emerald-500" />
                                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Core Fields</p>
                                {isEditing && <span className="text-[10px] text-amber-500 font-medium ml-auto">Click any field to edit</span>}
                            </div>

                            {/* Input data section */}
                            <div className="bg-amber-50/60 rounded-xl p-4 border border-amber-100 space-y-0">
                                <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider mb-2">Input Data</p>
                                {inputFields.map(f => (
                                    <EditableField
                                        key={f.key}
                                        label={f.label}
                                        value={f.value}
                                        isEditing={isEditing}
                                        onChange={(val) => handleFieldChange(f.key, val)}
                                        isUrl={f.isUrl}
                                        isModified={isFieldModified(f.key)}
                                        isInput={true}
                                    />
                                ))}
                            </div>

                            {/* AI output fields */}
                            <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
                                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider px-4 pt-3 pb-1">AI Output</p>
                                {outputFields.map(f => (
                                    <EditableField
                                        key={f.key}
                                        label={f.label}
                                        value={f.value}
                                        isEditing={isEditing}
                                        onChange={(val) => handleFieldChange(f.key, val)}
                                        isUrl={f.isUrl}
                                        isModified={isFieldModified(f.key)}
                                        isInput={false}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Right: Properties */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 mb-1">
                                <Layers className="w-4 h-4 text-violet-500" />
                                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                                    Properties ({propPairs.length})
                                </p>
                                {isEditing && (
                                    <button onClick={addProperty}
                                        className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold
                                                   bg-violet-50 text-violet-600 hover:bg-violet-100 border border-violet-200 transition-all">
                                        <Plus className="w-3.5 h-3.5" /> Add
                                    </button>
                                )}
                            </div>

                            {propPairs.length > 0 ? (
                                <div className="rounded-xl overflow-hidden border border-slate-200">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="bg-violet-50/60 border-b border-slate-200">
                                                <th className="px-4 py-2.5 text-left text-[10px] font-bold text-violet-600 uppercase tracking-wider w-[40%]">Property</th>
                                                <th className="px-4 py-2.5 text-left text-[10px] font-bold text-violet-600 uppercase tracking-wider">Value</th>
                                                {isEditing && <th className="px-2 py-2.5 w-10"></th>}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {propPairs.map((pf, i) => (
                                                <tr key={i} className="hover:bg-slate-50/60 transition-colors">
                                                    <td className="px-4 py-2">
                                                        {isEditing ? (
                                                            <input
                                                                type="text"
                                                                value={pf.name}
                                                                onChange={(e) => handlePropChange(i, 'name', e.target.value)}
                                                                className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-700
                                                                           focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400 transition-all"
                                                                placeholder="Property name"
                                                            />
                                                        ) : (
                                                            <span className="text-slate-700 font-medium text-xs">{pf.name}</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        {isEditing ? (
                                                            <input
                                                                type="text"
                                                                value={pf.value}
                                                                onChange={(e) => handlePropChange(i, 'value', e.target.value)}
                                                                className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-800
                                                                           focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400 transition-all"
                                                                placeholder="Value"
                                                            />
                                                        ) : (
                                                            <span className="text-slate-800 text-sm break-all">{pf.value || '—'}</span>
                                                        )}
                                                    </td>
                                                    {isEditing && (
                                                        <td className="px-2 py-2 text-center">
                                                            <button onClick={() => removeProperty(i)}
                                                                className="p-1 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                                                                title="Remove property">
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="rounded-xl border border-dashed border-slate-200 py-12 text-center">
                                    <Layers className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                                    <p className="text-sm text-slate-400">No properties for this record</p>
                                    {isEditing && (
                                        <button onClick={addProperty}
                                            className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold
                                                       bg-violet-50 text-violet-600 hover:bg-violet-100 border border-violet-200 transition-all">
                                            <Plus className="w-3.5 h-3.5" /> Add Property
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* ── Footer nav ───────────────────────────── */}
                <div className="shrink-0 flex items-center justify-between px-6 py-3 bg-slate-50 border-t border-slate-200">
                    <p className="text-xs text-slate-500">
                        {isEditing ? (
                            <span className="text-amber-600 font-medium">Edit mode — modify fields and click Save</span>
                        ) : (
                            <>
                                Use <kbd className="px-1.5 py-0.5 bg-white rounded border text-[10px] font-mono">←</kbd>
                                <kbd className="px-1.5 py-0.5 bg-white rounded border text-[10px] font-mono ml-1">→</kbd> to navigate ·
                                <kbd className="px-1.5 py-0.5 bg-white rounded border text-[10px] font-mono ml-1">Esc</kbd> to close
                            </>
                        )}
                    </p>
                    <div className="flex items-center gap-2">
                        {isEditing && hasChanges() && (
                            <span className="text-xs text-amber-600 font-medium mr-2">● Unsaved changes</span>
                        )}
                        <button onClick={() => handleNavigate(currentIndex - 1)} disabled={!hasPrev}
                            className="px-4 py-1.5 rounded-lg text-xs font-semibold bg-white border border-slate-200 text-slate-600
                                       hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
                            ← Previous
                        </button>
                        <span className="text-xs text-slate-500 font-mono min-w-[60px] text-center">{currentIndex + 1} / {records.length}</span>
                        <button onClick={() => handleNavigate(currentIndex + 1)} disabled={!hasNext}
                            className="px-4 py-1.5 rounded-lg text-xs font-semibold bg-white border border-slate-200 text-slate-600
                                       hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
                            Next →
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ══════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════
export default function SourceDataPage() {
    const navigate = useNavigate();

    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [sortField, setSortField] = useState('id');
    const [sortDir, setSortDir] = useState('desc');
    const [detailIndex, setDetailIndex] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [importing, setImporting] = useState(false);
    const [importMode, setImportMode] = useState('skip');
    const [importStatus, setImportStatus] = useState(EMPTY_IMPORT_STATUS);
    const importInputRef = useRef(null);
    const [showFilters, setShowFilters] = useState(false);

    // Advanced filters
    const [filterPN, setFilterPN] = useState('');
    const [filterPNActive, setFilterPNActive] = useState('');
    const [filterMfr, setFilterMfr] = useState('');
    const [filterMfrActive, setFilterMfrActive] = useState('');
    const [filterVendor, setFilterVendor] = useState('');
    const [filterVendorActive, setFilterVendorActive] = useState('');

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchQuery(searchInput);
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    // Fetch records
    const fetchRecords = useCallback(async () => {
        setLoading(true);
        try {
            const result = await apiClient.listMajorRecords({
                page: currentPage,
                limit: pageSize,
                search: searchQuery,
                sortField,
                sortDir,
                filterPartNumber: filterPNActive,
                filterManufacturer: filterMfrActive,
                filterVendor: filterVendorActive,
            });
            setRecords(result.records || []);
            setTotal(result.total || 0);
            setTotalPages(result.totalPages || 1);
        } catch (err) {
            console.error('Failed to fetch source data:', err);
            toast.error('Failed to load source data');
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, searchQuery, sortField, sortDir, filterPNActive, filterMfrActive, filterVendorActive]);

    useEffect(() => { fetchRecords(); }, [fetchRecords]);

    // Sort handler
    const handleSort = (field) => {
        if (sortField === field) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDir('asc');
        }
        setCurrentPage(1);
    };

    // Apply filters
    const applyFilters = () => {
        setFilterPNActive(filterPN);
        setFilterMfrActive(filterMfr);
        setFilterVendorActive(filterVendor);
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setFilterPN('');
        setFilterMfr('');
        setFilterPNActive('');
        setFilterMfrActive('');
        setFilterVendor('');
        setFilterVendorActive('');
        setCurrentPage(1);
    };

    const hasActiveFilters = !!(filterPNActive || filterMfrActive || filterVendorActive);

    // Export — same format as Results page Excel export
    const handleExport = async () => {
        setExporting(true);
        try {
            const allRecords = [];
            let page = 1;
            let pages = 1;
            do {
                const result = await apiClient.listMajorRecords({
                    page, limit: 200, search: searchQuery, sortField, sortDir,
                    filterPartNumber: filterPNActive, filterManufacturer: filterMfrActive, filterVendor: filterVendorActive,
                });
                allRecords.push(...(result.records || []));
                pages = result.totalPages || 1;
                page++;
            } while (page <= pages);
            if (allRecords.length === 0) { toast.warning('No data to export'); return; }

            const rows = allRecords.map(rec => EXPORT_HEADERS.map(h => h === 'ID' ? formatSourceId(rec) : (rec[h] ?? '')));
            const ws = XLSX.utils.aoa_to_sheet([EXPORT_HEADERS, ...rows]);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Source Data');
            XLSX.writeFile(wb, `banyan-source-data-${new Date().toISOString().slice(0, 10)}.xlsx`);
            toast.success(`Exported ${allRecords.length} records`);
        } catch (err) {
            console.error('Export failed:', err);
            toast.error('Export failed');
        } finally {
            setExporting(false);
        }
    };

    const downloadTemplate = () => {
        const ws = XLSX.utils.aoa_to_sheet([EXPORT_HEADERS.filter(h => h !== 'ID')]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Source Data');
        XLSX.writeFile(wb, 'banyan-source-data-import-template.xlsx');
    };

    const runImport = async (recordsToImport, overwriteDuplicates, title = 'Import source data') => {
        setImporting(true);
        setImportStatus({ ...EMPTY_IMPORT_STATUS, open: true, running: true, total: recordsToImport.length, title });
        const aggregate = { processed: 0, inserted: 0, updated: 0, skipped: 0, failed: 0, skippedRows: [], failedRows: [] };
        try {
            for (let offset = 0; offset < recordsToImport.length; offset += IMPORT_BATCH_SIZE) {
                const batch = recordsToImport.slice(offset, offset + IMPORT_BATCH_SIZE);
                try {
                    const result = await apiClient.importMajorRecords(batch, overwriteDuplicates);
                    aggregate.inserted += result.inserted || 0;
                    aggregate.updated += result.updated || 0;
                    aggregate.skipped += result.skipped || 0;
                    aggregate.failed += result.failed || 0;
                    for (const outcome of result.outcomes || []) {
                        const entry = { record: batch[outcome.index], row: offset + outcome.index + 2, reason: outcome.reason, duplicate: outcome.duplicate || null };
                        if (outcome.action === 'skipped') aggregate.skippedRows.push(entry);
                        if (outcome.action === 'failed') aggregate.failedRows.push(entry);
                    }
                } catch (error) {
                    aggregate.failed += batch.length;
                    batch.forEach((record, index) => aggregate.failedRows.push({ record, row: offset + index + 2, reason: error.message }));
                }
                aggregate.processed += batch.length;
                setImportStatus(prev => ({ ...prev, ...aggregate, skippedRows: [...aggregate.skippedRows], failedRows: [...aggregate.failedRows] }));
            }
            setCurrentPage(1);
            await fetchRecords();
            toast.success(`Import completed: ${aggregate.inserted} added, ${aggregate.updated} updated, ${aggregate.skipped} skipped, ${aggregate.failed} failed`);
        } catch (err) {
            console.error('Import failed:', err);
            toast.error(`Import failed: ${err.message}`);
        } finally {
            setImporting(false);
            setImportStatus(prev => ({ ...prev, running: false }));
        }
    };

    const handleImport = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        try {
            const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: false });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            if (!sheet) throw new Error('The workbook has no worksheets');
            const parsed = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false });
            const allowed = new Set([...EXPORT_HEADERS.filter(h => h !== 'ID'), 'Description']);
            const recordsToImport = parsed.map(row => Object.fromEntries(
                Object.entries(row)
                    .map(([key, value]) => [canonicalImportHeader(key), value])
                    .filter(([key, value]) => allowed.has(key) && String(value).trim() !== '')
            )).map(row => {
                if (!row['Description 1'] && row.Description) row['Description 1'] = row.Description;
                delete row.Description;
                return row;
            }).filter(row => Object.keys(row).length > 0);
            if (!recordsToImport.length) throw new Error('No valid source-data rows were found. Use the provided template headers.');
            if (recordsToImport.length > 5000) throw new Error('Import files are limited to 5,000 rows at a time');
            await runImport(recordsToImport, importMode === 'overwrite');
        } catch (err) {
            toast.error(`Import failed: ${err.message}`);
        }
    };

    const rerunRows = async (rows, overwrite, title) => {
        if (!rows.length || importing) return;
        await runImport(rows.map(item => item.record), overwrite, title);
    };

    const pageRecordIds = records.map(rec => Number(rec.id ?? rec.ID)).filter(Boolean);
    const allPageSelected = pageRecordIds.length > 0 && pageRecordIds.every(id => selectedIds.has(id));
    const toggleSelectAll = () => setSelectedIds(prev => {
        const next = new Set(prev);
        if (allPageSelected) pageRecordIds.forEach(id => next.delete(id));
        else pageRecordIds.forEach(id => next.add(id));
        return next;
    });
    const toggleSelected = (id) => setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const handleBulkDelete = async () => {
        const ids = [...selectedIds];
        if (!ids.length || !window.confirm(`Permanently delete ${ids.length} selected source record(s)?\n\nThis action cannot be undone.`)) return;
        setBulkDeleting(true);
        try {
            const result = await apiClient.bulkDeleteMajorRecordsById(ids);
            toast.success(`Deleted ${result.deleted} source record(s)`);
            setSelectedIds(new Set());
            await fetchRecords();
        } catch (error) {
            toast.error(`Bulk delete failed: ${error.message}`);
        } finally {
            setBulkDeleting(false);
        }
    };

    return (
        <div className="flex flex-col lg:flex-row gap-0 h-[calc(100vh-80px)] animate-fade-in">

            {importStatus.open && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
                    <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
                            <div><h3 className="text-lg font-bold text-slate-800">{importStatus.title}</h3><p className="text-xs text-slate-500 mt-0.5">{importStatus.running ? 'Import is running in safe batches of 100 records.' : 'Import processing is complete.'}</p></div>
                            <button onClick={() => !importStatus.running && setImportStatus(EMPTY_IMPORT_STATUS)} disabled={importStatus.running} className="p-2 rounded-lg text-slate-400 hover:bg-slate-200 disabled:opacity-30"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-6 space-y-5">
                            <div>
                                <div className="flex justify-between text-sm mb-2"><span className="font-semibold text-slate-700">{importStatus.processed.toLocaleString()} / {importStatus.total.toLocaleString()} processed</span><span className="text-slate-500">{Math.max(0, importStatus.total - importStatus.processed).toLocaleString()} remaining</span></div>
                                <div className="h-3 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-gradient-to-r from-primary-500 to-cyan-500 transition-all duration-300" style={{ width: `${importStatus.total ? Math.round(importStatus.processed / importStatus.total * 100) : 0}%` }} /></div>
                                <p className="text-right text-xs font-semibold text-primary-600 mt-1">{importStatus.total ? Math.round(importStatus.processed / importStatus.total * 100) : 0}%</p>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3"><p className="text-2xl font-bold text-emerald-700">{(importStatus.inserted + importStatus.updated).toLocaleString()}</p><p className="text-xs font-semibold text-emerald-600">Successful</p><p className="text-[10px] text-emerald-600 mt-1">{importStatus.inserted} added · {importStatus.updated} updated</p></div>
                                <div className="rounded-xl bg-blue-50 border border-blue-200 p-3"><p className="text-2xl font-bold text-blue-700">{importStatus.updated.toLocaleString()}</p><p className="text-xs font-semibold text-blue-600">Updated</p></div>
                                <div className="rounded-xl bg-amber-50 border border-amber-200 p-3"><p className="text-2xl font-bold text-amber-700">{importStatus.skipped.toLocaleString()}</p><p className="text-xs font-semibold text-amber-600">Skipped</p></div>
                                <div className="rounded-xl bg-red-50 border border-red-200 p-3"><p className="text-2xl font-bold text-red-700">{importStatus.failed.toLocaleString()}</p><p className="text-xs font-semibold text-red-600">Failed</p></div>
                            </div>
                            {!importStatus.running && (importStatus.skippedRows.length > 0 || importStatus.failedRows.length > 0) && (
                                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                                    <p className="text-sm font-semibold text-slate-700 mb-3">Items needing attention</p>
                                    <div className="max-h-72 overflow-auto space-y-2 mb-4">{[...importStatus.failedRows, ...importStatus.skippedRows].map((item, index) => {
                                        const db = item.duplicate?.existingData || {};
                                        const incoming = item.duplicate?.incomingData || item.record || {};
                                        return <div key={index} className="text-xs p-3 rounded-lg border border-slate-200 bg-white">
                                            <div className="flex gap-3"><span className="font-mono font-semibold text-slate-500 w-16 shrink-0">Row {item.row}</span><span className="text-slate-800 font-semibold break-words">{item.reason}</span></div>
                                            {item.duplicate && <div className="mt-2 ml-[76px] grid sm:grid-cols-2 gap-2 text-[11px]">
                                                <div className="rounded-md bg-amber-50 border border-amber-100 p-2">
                                                    <p className="font-bold text-amber-700 mb-1">Excel row</p>
                                                    <p><b>{item.duplicate.incomingColumn}:</b> {item.duplicate.matchedValue || '—'}</p>
                                                    <p><b>Description:</b> {incoming['Description 1'] || incoming.Description || '—'}</p>
                                                    <p><b>Manufacturer:</b> {incoming['MANUFACTURER NAME 1'] || '—'}</p>
                                                    <p><b>Vendor:</b> {incoming['VENDOR NAME'] || '—'}</p>
                                                    <p><b>MFR PNs:</b> {[incoming['MANUFACTURER PART NUMBER 1'], incoming['MANUFACTURER PART NUMBER 2'], incoming['MANUFACTURER PART NUMBER 3']].filter(Boolean).join(', ') || '—'}</p>
                                                    <p><b>Vendor PNs:</b> {[incoming['VENDOR PART NUMBER'], incoming['VENDOR PART NUMBER 2'], incoming['VENDOR PART NUMBER 3'], incoming['VENDOR PART NUMBER 4'], incoming['VENDOR PART NUMBER 5']].filter(Boolean).join(', ') || '—'}</p>
                                                </div>
                                                <div className="rounded-md bg-blue-50 border border-blue-100 p-2">
                                                    <p className="font-bold text-blue-700 mb-1">Existing {formatSourceId({ id: item.duplicate.existingId })}</p>
                                                    <p><b>{item.duplicate.existingColumn}:</b> {item.duplicate.matchedValue || '—'}</p>
                                                    <p><b>Description:</b> {db['Description 1'] || db.Description || '—'}</p>
                                                    <p><b>Manufacturer:</b> {db['MANUFACTURER NAME 1'] || '—'}</p>
                                                    <p><b>Vendor:</b> {db['VENDOR NAME'] || '—'}</p>
                                                    <p><b>MFR PNs:</b> {[db['MANUFACTURER PART NUMBER 1'], db['MANUFACTURER PART NUMBER 2'], db['MANUFACTURER PART NUMBER 3']].filter(Boolean).join(', ') || '—'}</p>
                                                    <p><b>Vendor PNs:</b> {[db['VENDOR PART NUMBER'], db['VENDOR PART NUMBER 2'], db['VENDOR PART NUMBER 3'], db['VENDOR PART NUMBER 4'], db['VENDOR PART NUMBER 5']].filter(Boolean).join(', ') || '—'}</p>
                                                </div>
                                            </div>}
                                        </div>;
                                    })}</div>
                                    <div className="flex flex-wrap gap-2">
                                        {importStatus.failedRows.length > 0 && <button onClick={() => rerunRows(importStatus.failedRows, importMode === 'overwrite', 'Rerun failed items')} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold bg-red-600 text-white hover:bg-red-700"><RotateCcw className="w-3.5 h-3.5" /> Rerun {importStatus.failedRows.length} failed</button>}
                                        {importStatus.skippedRows.length > 0 && <button onClick={() => rerunRows(importStatus.skippedRows, true, 'Rerun skipped items with overwrite')} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold bg-amber-600 text-white hover:bg-amber-700"><RotateCcw className="w-3.5 h-3.5" /> Rerun {importStatus.skippedRows.length} skipped with overwrite</button>}
                                    </div>
                                </div>
                            )}
                            {!importStatus.running && <div className="flex justify-end"><button onClick={() => setImportStatus(EMPTY_IMPORT_STATUS)} className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-slate-800 text-white hover:bg-slate-900">Close</button></div>}
                        </div>
                    </div>
                </div>
            )}

            {/* ── LEFT SIDEBAR ─────────────────────────────────── */}
            <aside className="lg:w-[250px] shrink-0 bg-white/90 backdrop-blur-lg border-r border-slate-200/60 p-4 flex flex-col gap-4 lg:overflow-y-auto">
                {/* Stats */}
                <div className="space-y-3">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Database</p>
                    <div className="relative rounded-xl p-3.5 bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-md">
                        <div className="absolute top-2.5 right-2.5 bg-white/20 p-1.5 rounded-lg">
                            <Database className="w-4 h-4 text-white/80" />
                        </div>
                        <p className="text-2xl font-extrabold">{total.toLocaleString()}</p>
                        <p className="text-xs font-medium text-white/75 mt-0.5">Total Records</p>
                    </div>
                </div>

                {/* ── Filters ─────────────────────────────── */}
                <div className="space-y-2">
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className="w-full flex items-center justify-between text-xs font-bold text-slate-400 uppercase tracking-wider py-1"
                    >
                        <span className="flex items-center gap-1.5">
                            <Filter className="w-3.5 h-3.5" />
                            Filters
                            {hasActiveFilters && (
                                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-primary-500 text-white text-[9px] font-bold">
                                    {(filterPNActive ? 1 : 0) + (filterMfrActive ? 1 : 0) + (filterVendorActive ? 1 : 0)}
                                </span>
                            )}
                        </span>
                        {showFilters ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    </button>

                    {showFilters && (
                        <div className="space-y-3 p-3 bg-slate-50/80 rounded-xl border border-slate-200/60 animate-fade-in">
                            <div>
                                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Part Number</label>
                                <input
                                    type="text"
                                    value={filterPN}
                                    onChange={e => setFilterPN(e.target.value)}
                                    placeholder="e.g. NK 35/30"
                                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm
                                               focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400 transition-all"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Manufacturer</label>
                                <input
                                    type="text"
                                    value={filterMfr}
                                    onChange={e => setFilterMfr(e.target.value)}
                                    placeholder="e.g. SKF"
                                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm
                                               focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400 transition-all"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Vendor</label>
                                <input type="text" value={filterVendor} onChange={e => setFilterVendor(e.target.value)} placeholder="e.g. Grainger"
                                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400 transition-all" />
                            </div>
                            <div className="flex gap-2">
                                <button onClick={applyFilters}
                                    className="flex-1 px-3 py-2 rounded-lg text-xs font-semibold bg-primary-600 text-white hover:bg-primary-700 transition-colors shadow-sm">
                                    Apply
                                </button>
                                {hasActiveFilters && (
                                    <button onClick={clearFilters}
                                        className="px-3 py-2 rounded-lg text-xs font-semibold bg-white border border-slate-200 text-slate-600 hover:bg-slate-100 transition-colors">
                                        Clear
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Actions ─────────────────────────────── */}
                <div className="space-y-2 mt-auto">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Actions</p>
                    {selectedIds.size > 0 && (
                        <button onClick={handleBulkDelete} disabled={bulkDeleting}
                            className="w-full text-sm py-2.5 justify-start inline-flex items-center gap-2 px-4 rounded-xl font-semibold bg-red-600 text-white hover:bg-red-700 shadow-md disabled:opacity-50">
                            {bulkDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            Delete selected ({selectedIds.size})
                        </button>
                    )}
                    <input ref={importInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleImport} className="hidden" />
                    <div className="w-full px-3 py-2 rounded-xl text-xs font-medium bg-emerald-50 border border-emerald-200 text-emerald-700">
                        Duplicate part numbers are accepted. Source ID is the only unique field.
                    </div>
                    <button onClick={() => importInputRef.current?.click()} disabled={importing}
                        className="w-full text-sm py-2.5 justify-start inline-flex items-center gap-2 px-4 rounded-xl font-semibold bg-primary-600 text-white hover:bg-primary-700 shadow-md disabled:opacity-50 disabled:cursor-not-allowed">
                        {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        {importing ? 'Importing...' : 'Import Excel / CSV'}
                    </button>
                    <button
                        onClick={handleExport}
                        disabled={exporting || total === 0}
                        className="w-full text-sm !py-2.5 justify-start inline-flex items-center gap-2
                                   px-4 rounded-xl font-semibold transition-all duration-200
                                   bg-gradient-to-r from-emerald-500 to-teal-500 text-white
                                   hover:from-emerald-600 hover:to-teal-600 shadow-md hover:shadow-lg
                                   disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <FileSpreadsheet className="w-4 h-4" />
                        {exporting ? 'Exporting...' : 'Export Excel'}
                    </button>
                    <button onClick={downloadTemplate} disabled={importing}
                        className="w-full text-xs py-2 justify-start inline-flex items-center gap-2 px-4 rounded-xl font-semibold bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                        <Download className="w-3.5 h-3.5" /> Download import template
                    </button>
                    <button
                        onClick={() => navigate('/results')}
                        className="w-full btn-secondary text-sm !py-2.5 justify-start"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        Back to Results
                    </button>
                </div>
            </aside>

            {/* ── RIGHT CONTENT AREA ──────────────────────────── */}
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

                {/* ── Top bar ─────────────────────────────────── */}
                <div className="shrink-0 bg-white/80 backdrop-blur-lg border-b border-slate-200/60 px-4 py-3">
                    <div className="flex items-center gap-3 overflow-x-auto scrollbar-thin">
                        <div className="relative flex-1 min-w-[200px]">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input
                                type="text"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder="Search across all fields…"
                                className="w-full pl-10 pr-4 py-2.5 bg-slate-50/80 border border-slate-200 rounded-xl
                                           text-sm text-slate-800 placeholder:text-slate-400
                                           focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400
                                           transition-all duration-200"
                            />
                            {searchInput && (
                                <button onClick={() => setSearchInput('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                    <XCircle className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                        <button onClick={fetchRecords} disabled={loading}
                            className="shrink-0 p-2.5 rounded-xl border border-slate-200 bg-slate-50/80
                                       text-slate-500 hover:text-primary-600 hover:bg-primary-50
                                       transition-all duration-200 disabled:opacity-50"
                            title="Refresh data">
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                        <span className="shrink-0 text-xs text-slate-500 whitespace-nowrap">
                            {total.toLocaleString()} records
                        </span>
                    </div>

                    {/* Active filter badges */}
                    {(searchQuery || hasActiveFilters) && (
                        <div className="flex items-center gap-2 mt-2.5 flex-wrap">
                            {searchQuery && (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700 ring-1 ring-primary-200">
                                    Search: &quot;{searchQuery}&quot;
                                    <button onClick={() => { setSearchInput(''); setSearchQuery(''); }}><XCircle className="w-3.5 h-3.5" /></button>
                                </span>
                            )}
                            {filterPNActive && (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-violet-50 text-violet-700 ring-1 ring-violet-200">
                                    PN: &quot;{filterPNActive}&quot;
                                    <button onClick={() => { setFilterPN(''); setFilterPNActive(''); }}><XCircle className="w-3.5 h-3.5" /></button>
                                </span>
                            )}
                            {filterMfrActive && (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 ring-1 ring-amber-200">
                                    MFR: &quot;{filterMfrActive}&quot;
                                    <button onClick={() => { setFilterMfr(''); setFilterMfrActive(''); }}><XCircle className="w-3.5 h-3.5" /></button>
                                </span>
                            )}
                            {filterVendorActive && (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-cyan-50 text-cyan-700 ring-1 ring-cyan-200">
                                    Vendor: &quot;{filterVendorActive}&quot;
                                    <button onClick={() => { setFilterVendor(''); setFilterVendorActive(''); }}><XCircle className="w-3.5 h-3.5" /></button>
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Table ────────────────────────────────── */}
                <div className="flex-1 flex flex-col min-h-0 bg-white">
                    {loading ? (
                        <div className="flex items-center justify-center flex-1">
                            <div className="text-center space-y-4">
                                <Loader2 className="w-10 h-10 text-primary-500 animate-spin mx-auto" />
                                <p className="text-slate-500 font-medium">Loading records…</p>
                            </div>
                        </div>
                    ) : records.length === 0 ? (
                        <div className="flex items-center justify-center flex-1">
                            <div className="text-center space-y-3">
                                <Database className="w-12 h-12 text-slate-300 mx-auto" />
                                <p className="text-slate-500 font-medium">
                                    {searchQuery || hasActiveFilters ? 'No records match your search/filters' : 'No records in database yet'}
                                </p>
                                <p className="text-xs text-slate-400">
                                    {searchQuery || hasActiveFilters ? 'Try different search terms or clear filters' : 'Save items from Results page to see them here'}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="flex-1 overflow-auto">
                                <table className="w-full text-sm min-w-[1400px]">
                                    <thead className="sticky top-0 z-10">
                                        <tr className="border-b border-slate-200 bg-slate-50/95 backdrop-blur-sm">
                                            <th className="px-3 py-3 text-center w-10"><input type="checkbox" checked={allPageSelected} onChange={toggleSelectAll} className="w-4 h-4 accent-primary-600" title="Select all records on this page" /></th>
                                            <th onClick={() => handleSort('id')} className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap cursor-pointer select-none">Source ID</th>
                                            {TABLE_COLUMNS.map(col => (
                                                <th
                                                    key={col.key}
                                                    onClick={() => col.sortable && handleSort(col.key)}
                                                    className={`px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider
                                                                ${col.sortable ? 'cursor-pointer hover:text-slate-700' : ''}
                                                                select-none transition-colors whitespace-nowrap`}
                                                >
                                                    <span className="inline-flex items-center gap-1">
                                                        {col.label}
                                                        {col.sortable && (
                                                            <ArrowUpDown className={`w-3.5 h-3.5 transition-colors
                                                                ${sortField === col.key ? 'text-primary-500' : 'text-slate-300'}`} />
                                                        )}
                                                    </span>
                                                </th>
                                            ))}
                                            <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-14">Props</th>
                                            <th className="px-3 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider w-14">View</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {records.map((rec, idx) => {
                                            const recordId = Number(rec.id ?? rec.ID);
                                            const propCount = getPropertyPairs(rec).length;
                                            return (
                                                <tr key={rec.id || rec.ID || idx}
                                                    className="group hover:bg-primary-50/40 transition-colors cursor-pointer"
                                                    onClick={() => setDetailIndex(idx)}>
                                                    <td className="px-3 py-2.5 text-center" onClick={e => e.stopPropagation()}><input type="checkbox" checked={selectedIds.has(recordId)} onChange={() => toggleSelected(recordId)} className="w-4 h-4 accent-primary-600" /></td>
                                                    <td className="px-3 py-2.5 text-primary-700 font-mono text-xs font-semibold whitespace-nowrap">{formatSourceId(rec)}</td>

                                                    {TABLE_COLUMNS.map(col => (
                                                        <td key={col.key} className="px-3 py-2.5 text-slate-700">
                                                            {col.isUrl ? (
                                                                <UrlCell url={rec[col.key]} />
                                                            ) : col.bold ? (
                                                                <span className="font-semibold text-slate-800">
                                                                    <TruncatedCell text={rec[col.key]} maxLen={col.maxLen || 30} />
                                                                </span>
                                                            ) : (
                                                                <TruncatedCell text={rec[col.key]} maxLen={col.maxLen || 30} />
                                                            )}
                                                        </td>
                                                    ))}

                                                    <td className="px-3 py-2.5">
                                                        {propCount > 0 && (
                                                            <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-[10px] font-bold
                                                                             bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200 min-w-[24px]">
                                                                {propCount}
                                                            </span>
                                                        )}
                                                    </td>

                                                    <td className="px-3 py-2.5 text-center">
                                                        <button onClick={(e) => { e.stopPropagation(); setDetailIndex(idx); }}
                                                            className="p-1.5 rounded-lg transition-colors text-slate-400
                                                                       hover:bg-primary-50 hover:text-primary-600"
                                                            title="View / Edit record">
                                                            <Eye size={15} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* ── Pagination ──────────────────────────── */}
                            <div className="shrink-0 flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 bg-slate-50/60 border-t border-slate-100">
                                <p className="text-xs text-slate-500">
                                    Showing{' '}
                                    <span className="font-semibold text-slate-700">{(currentPage - 1) * pageSize + 1}</span>
                                    –<span className="font-semibold text-slate-700">{Math.min(currentPage * pageSize, total)}</span>
                                    {' '}of <span className="font-semibold text-slate-700">{total.toLocaleString()}</span>
                                </p>
                                <div className="flex items-center gap-3">
                                    <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); setSelectedIds(new Set()); }}
                                        className="px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-600 focus:outline-none focus:ring-2 focus:ring-primary-200">
                                        {PAGE_SIZE_OPTIONS.map(size => <option key={size} value={size}>{size} per page</option>)}
                                    </select>
                                    <div className="flex items-center gap-1">
                                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        className="p-2 rounded-lg text-slate-500 hover:bg-slate-200/60 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
                                        <ChevronLeft className="w-4 h-4" />
                                    </button>
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let page;
                                        if (totalPages <= 5) page = i + 1;
                                        else if (currentPage <= 3) page = i + 1;
                                        else if (currentPage >= totalPages - 2) page = totalPages - 4 + i;
                                        else page = currentPage - 2 + i;
                                        return (
                                            <button key={page} onClick={() => setCurrentPage(page)}
                                                className={`w-8 h-8 rounded-lg text-xs font-semibold transition-all
                                                    ${currentPage === page
                                                        ? 'bg-primary-600 text-white shadow-sm'
                                                        : 'text-slate-600 hover:bg-slate-200/60'}`}>
                                                {page}
                                            </button>
                                        );
                                    })}
                                    <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                        className="p-2 rounded-lg text-slate-500 hover:bg-slate-200/60 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* ── Detail Modal with Prev/Next + Editing ──────────────────── */}
            {detailIndex !== null && records[detailIndex] && (
                <RecordDetailModal
                    records={records}
                    currentIndex={detailIndex}
                    onClose={() => setDetailIndex(null)}
                    onNavigate={(idx) => setDetailIndex(idx)}
                    onSave={fetchRecords}
                    onDelete={fetchRecords}
                />
            )}
        </div>
    );
}
